import { useState, useEffect, useCallback } from 'react';
import programApi from './programApi';

export const usePrograms = (tenantId, shouldFetch = true) => {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchPrograms = useCallback(async () => {
    if (!tenantId || !shouldFetch) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await programApi.getPrograms(tenantId);
      setPrograms(response.data.programs || []);
    } catch (err) {
      console.error('Error fetching programs:', err);
      setError(
        err.response?.data?.message ||
        'Unable to load programs. Please try again.'
      );
      setPrograms([]);
    } finally {
      setLoading(false);
    }
  }, [tenantId, shouldFetch]);

  const createOrUpdateProgram = async (formData, editingProgram = null) => {
    try {
      setSaving(true);
      setError(null);

      const data = {
        tenantId,
        programTypeId: formData.programTypeId,
        name: formData.name,
        code: formData.code,
        description: formData.description,
        duration: parseInt(formData.duration),
        totalSemesters: parseInt(formData.totalSemesters),
        status: formData.status
      };

      let response;
      if (editingProgram) {
        response = await programApi.updateProgram(editingProgram._id, data);
      } else {
        response = await programApi.createProgram(data);
      }

      await fetchPrograms(); // Refresh the list
      return response.data;
    } catch (err) {
      console.error('Error saving program:', err);
      const errorMessage = err.response?.data?.message || 'Error saving program';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const updateProgramStatus = async (program, newStatus) => {
    try {
      setSaving(true);
      setError(null);

      const response = await programApi.changeProgramStatus(program._id, {
        tenantId,
        status: newStatus
      });

      await fetchPrograms(); // Refresh the list
      return response.data;
    } catch (err) {
      console.error('Error updating program status:', err);
      const errorMessage = err.response?.data?.message || 'Error updating program status';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    fetchPrograms();
  }, [fetchPrograms]);

  return {
    programs,
    loading,
    error,
    saving,
    fetchPrograms,
    createOrUpdateProgram,
    updateProgramStatus
  };
};

export default usePrograms;